import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  CheckoutElementsProvider,
  CurrencySelectorElement,
  PaymentElement,
  useCheckoutElements,
} from "@stripe/react-stripe-js/checkout";
import {
  ArrowLeft,
  Check,
  CreditCard,
  Loader2,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { FlowguardLogo } from "@/components/FlowguardLogo";
import { PUBLIC_SITE_URL } from "@/utils/publicSite";

const PLAN_DETAILS = {
  starter: {
    name: "Starter Investor",
    price: 9.99,
    accent: "emerald",
    description: "Save more deals and create polished Flowguard reports.",
    features: [
      "Save up to 50 deals",
      "5 PDF reports per month",
      "Rental and BRRRR workflows",
      "Deal notes and saved assumptions",
    ],
  },
  pro: {
    name: "Pro Investor",
    price: 29.99,
    accent: "amber",
    description: "Run high-volume underwriting with advanced workflows.",
    features: [
      "Unlimited saved deals",
      "Advanced analysis tools",
      "Deal comparison and portfolio workflows",
      "White-label investor-ready reports",
    ],
  },
};

const stripeAppearance = {
  theme: "night",
  variables: {
    colorPrimary: "#f59e0b",
    colorBackground: "#111c30",
    colorText: "#f8fafc",
    colorTextSecondary: "#94a3b8",
    colorDanger: "#fb7185",
    colorIcon: "#f59e0b",
    borderRadius: "12px",
    fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": {
      border: "1px solid #334155",
      boxShadow: "none",
    },
    ".Input:focus": {
      border: "1px solid #f59e0b",
      boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.14)",
    },
    ".Label": {
      color: "#cbd5e1",
      fontWeight: "600",
    },
  },
};

const createCheckoutRequestId = () =>
  globalThis.crypto?.randomUUID?.()
  || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });

const StripeCheckoutForm = ({ plan, email, onSessionChange }) => {
  const checkoutState = useCheckoutElements();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const activeCheckout = checkoutState.type === "success" ? checkoutState.checkout : null;
  const sessionFingerprint = activeCheckout
    ? JSON.stringify({
        currency: activeCheckout.currency,
        lineItems: activeCheckout.lineItems,
        recurring: activeCheckout.recurring,
        total: activeCheckout.total,
      })
    : "";

  useEffect(() => {
    if (activeCheckout) {
      onSessionChange(activeCheckout);
    }
    // Stripe replaces its formatted amount strings whenever the selected currency changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionFingerprint, onSessionChange]);

  if (checkoutState.type === "loading") {
    return <CheckoutSkeleton />;
  }

  if (checkoutState.type === "error") {
    return (
      <div role="alert" className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5">
        <h3 className="font-semibold text-rose-100">Payment form unavailable</h3>
        <p className="mt-2 text-sm leading-6 text-rose-200/80">{checkoutState.error.message}</p>
      </div>
    );
  }

  const checkout = checkoutState.checkout;
  const localizedTotal = checkout.total.total.amount;
  const hasCurrencyOptions = Boolean(checkout.currencyOptions?.length);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!checkout.canConfirm || submitting) return;

    setSubmitting(true);
    setError("");
    sessionStorage.setItem(
      "flowguard_pending_upgrade",
      JSON.stringify({ plan: plan.tier, provider: "stripe", createdAt: Date.now() })
    );

    try {
      // The backend sets the Checkout Session return_url. Supplying it again here
      // causes Stripe to reject confirmation before payment processing begins.
      const result = await checkout.confirm();

      if (result.type !== "error") {
        navigate("/dashboard?payment=processing&provider=stripe", { replace: true });
        return;
      }

      sessionStorage.removeItem("flowguard_pending_upgrade");
      const message = result.error?.message || "Payment could not be completed. Please try again.";
      setError(message);
      toast.error(message);
      setSubmitting(false);
    } catch (confirmError) {
      sessionStorage.removeItem("flowguard_pending_upgrade");
      const message = confirmError?.message || "Payment could not be completed. Please try again.";
      setError(message);
      toast.error(message);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-2xl border border-slate-700/80 bg-slate-950/35 p-4">
        <div className="mb-4 flex items-center justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Account</p>
            <p className="mt-1 break-all text-sm font-medium text-slate-200">{email}</p>
          </div>
          <LockKeyhole className="h-5 w-5 shrink-0 text-emerald-400" aria-hidden="true" />
        </div>
        {hasCurrencyOptions && (
          <div className="mb-5 border-b border-slate-800 pb-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Payment currency
            </p>
            <CurrencySelectorElement />
          </div>
        )}
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!checkout.canConfirm || submitting}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 font-bold text-slate-950 shadow-lg shadow-orange-950/30 transition hover:from-amber-300 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            Confirming securely...
          </>
        ) : (
          <>
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            Subscribe for {localizedTotal}/month
          </>
        )}
      </button>

      <p className="text-center text-xs leading-5 text-slate-500">
        By subscribing, you authorize recurring monthly charges until canceled. Payment details are
        securely handled by Stripe and never stored by Flowguard.
      </p>
    </form>
  );
};

const CheckoutSkeleton = () => (
  <div className="flex min-h-[360px] items-center justify-center rounded-3xl border border-slate-800 bg-slate-900/70">
    <div className="text-center">
      <Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-400" aria-hidden="true" />
      <p className="mt-4 text-sm text-slate-400">Preparing your secure checkout...</p>
    </div>
  </div>
);

export default function BillingCheckoutPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planKey = searchParams.get("plan")?.toLowerCase();
  const selectedPlan = PLAN_DETAILS[planKey];
  const requestIdRef = useRef(createCheckoutRequestId());
  const [checkoutData, setCheckoutData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [localizedSession, setLocalizedSession] = useState(null);

  const stripePromise = useMemo(() => {
    if (!checkoutData?.publishable_key) return null;
    return loadStripe(checkoutData.publishable_key);
  }, [checkoutData?.publishable_key]);

  useEffect(() => {
    if (!selectedPlan) {
      navigate("/upgrade", { replace: true });
      return;
    }

    let active = true;
    const initializeCheckout = async () => {
      setCheckoutData(null);
      setLoadError("");
      setLocalizedSession(null);
      try {
        const response = await axios.post(
          `${API}/billing/stripe/checkout-session`,
          {
            plan: planKey,
            interval: "month",
            checkout_request_id: requestIdRef.current,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (active) setCheckoutData(response.data);
      } catch (error) {
        const message = error.response?.data?.detail || "Secure checkout could not be initialized.";
        if (active) setLoadError(message);
      }
    };

    initializeCheckout();
    return () => {
      active = false;
    };
  }, [navigate, planKey, selectedPlan, token]);

  const changePlan = (nextPlan) => {
    if (nextPlan === planKey) return;
    requestIdRef.current = createCheckoutRequestId();
    navigate(`/billing/checkout?plan=${nextPlan}`);
  };

  const displayPlan = checkoutData?.plan || {
    ...selectedPlan,
    tier: planKey,
    interval: "month",
    currency: "usd",
  };
  const hasLocalizedAmount = (amount) => Boolean(amount && amount.minorUnitsAmount !== 0);
  const localizedLineItems = localizedSession?.lineItems || [];
  const localizedTotals = localizedSession?.total;
  const localizedRecurring = localizedSession?.recurring;

  return (
    <div className="min-h-screen overflow-hidden bg-[#081221] text-white">
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-24 top-20 h-80 w-80 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="absolute -right-32 top-1/3 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.025)_1px,transparent_1px)] bg-[size:48px_48px]" />
      </div>

      <header className="relative z-10 border-b border-slate-800/80 bg-[#081221]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/dashboard" aria-label="Return to dashboard">
            <FlowguardLogo size="md" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 text-sm text-slate-400 sm:flex">
              <LockKeyhole className="h-4 w-4 text-emerald-400" aria-hidden="true" />
              Secure checkout
            </div>
            <button
              type="button"
              onClick={() => navigate("/upgrade")}
              className="flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/70 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-slate-600 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to plans
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <div className="mb-9 max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-amber-300">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Upgrade your workflow
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Complete your Flowguard upgrade</h1>
          <p className="mt-3 max-w-xl leading-7 text-slate-400">
            Unlock the tools that turn a promising property into a clearly modeled investment decision.
          </p>
        </div>

        <div className="grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_420px]">
          <section className="rounded-3xl border border-slate-800 bg-slate-900/75 p-5 shadow-2xl shadow-black/20 backdrop-blur sm:p-7">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">Payment details</p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Pay securely with Stripe</h2>
                <p className="mt-2 text-sm leading-6 text-slate-400">
                  Your card details stay inside Stripe&apos;s PCI-compliant payment fields.
                </p>
              </div>
              <CreditCard className="mt-1 h-6 w-6 shrink-0 text-slate-500" aria-hidden="true" />
            </div>

            {loadError ? (
              <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5">
                <h3 className="font-semibold text-rose-100">Checkout unavailable</h3>
                <p className="mt-2 text-sm leading-6 text-rose-200/80">{loadError}</p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="mt-4 rounded-lg bg-rose-100 px-4 py-2 text-sm font-bold text-rose-950"
                >
                  Try again
                </button>
              </div>
            ) : checkoutData?.client_secret && stripePromise ? (
              <CheckoutElementsProvider
                key={checkoutData.client_secret}
                stripe={stripePromise}
                options={{
                  clientSecret: checkoutData.client_secret,
                  elementsOptions: { appearance: stripeAppearance },
                  adaptivePricing: { allowed: true },
                }}
              >
                <StripeCheckoutForm
                  plan={displayPlan}
                  email={user?.email}
                  onSessionChange={setLocalizedSession}
                />
              </CheckoutElementsProvider>
            ) : (
              <CheckoutSkeleton />
            )}
          </section>

          <aside className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/80 shadow-2xl shadow-black/20 lg:sticky lg:top-28">
            <div className={`h-1.5 ${planKey === "starter" ? "bg-emerald-400" : "bg-gradient-to-r from-amber-400 to-orange-500"}`} />
            <div className="p-6 sm:p-7">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Order summary</p>

              <div className="mt-5 grid grid-cols-2 gap-3" role="radiogroup" aria-label="Selected plan">
                {Object.entries(PLAN_DETAILS).map(([key, plan]) => {
                  const active = key === planKey;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => changePlan(key)}
                      className={`rounded-2xl border p-4 text-left transition ${
                        active
                          ? key === "starter"
                            ? "border-emerald-400 bg-emerald-400/10"
                            : "border-amber-400 bg-amber-400/10"
                          : "border-slate-700 bg-slate-950/30 hover:border-slate-600"
                      }`}
                    >
                      <span className="text-sm font-bold text-white">{plan.name.replace(" Investor", "")}</span>
                      <span className="mt-1 block text-xs text-slate-400">
                        {active && localizedTotals ? `${localizedTotals.total.amount}/mo` : "Select plan"}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 border-y border-slate-800 py-5">
                {localizedSession ? (
                  <>
                    <div className="space-y-4">
                      {localizedLineItems.map((lineItem) => (
                        <div key={lineItem.id} className="flex items-start justify-between gap-5">
                          <div>
                            <h2 className="font-bold text-white">{lineItem.name || displayPlan.name}</h2>
                            <p className="mt-1 text-sm leading-6 text-slate-400">
                              {lineItem.description || selectedPlan.description}
                            </p>
                            {lineItem.quantity > 1 && (
                              <p className="mt-1 text-xs text-slate-500">Quantity: {lineItem.quantity}</p>
                            )}
                          </div>
                          <span className="whitespace-nowrap text-xl font-bold text-white">
                            {lineItem.total.amount}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5 space-y-3 border-t border-slate-800 pt-4 text-sm text-slate-400">
                      <div className="flex items-center justify-between">
                        <span>Subtotal</span>
                        <span className="font-semibold text-slate-200">{localizedTotals.subtotal.amount}</span>
                      </div>
                      {hasLocalizedAmount(localizedTotals.discount) && (
                        <div className="flex items-center justify-between">
                          <span>Discount</span>
                          <span className="font-semibold text-emerald-300">{localizedTotals.discount.amount}</span>
                        </div>
                      )}
                      {hasLocalizedAmount(localizedTotals.shippingRate) && (
                        <div className="flex items-center justify-between">
                          <span>Shipping</span>
                          <span className="font-semibold text-slate-200">{localizedTotals.shippingRate.amount}</span>
                        </div>
                      )}
                      {hasLocalizedAmount(localizedTotals.surcharge) && (
                        <div className="flex items-center justify-between">
                          <span>Surcharge</span>
                          <span className="font-semibold text-slate-200">{localizedTotals.surcharge.amount}</span>
                        </div>
                      )}
                      {hasLocalizedAmount(localizedTotals.taxExclusive) && (
                        <div className="flex items-center justify-between">
                          <span>Tax</span>
                          <span className="font-semibold text-slate-200">{localizedTotals.taxExclusive.amount}</span>
                        </div>
                      )}
                      {hasLocalizedAmount(localizedTotals.taxInclusive) && (
                        <div className="flex items-center justify-between">
                          <span>Included tax</span>
                          <span className="font-semibold text-slate-200">{localizedTotals.taxInclusive.amount}</span>
                        </div>
                      )}
                      {hasLocalizedAmount(localizedTotals.appliedBalance) && (
                        <div className="flex items-center justify-between">
                          <span>Applied balance</span>
                          <span className="font-semibold text-emerald-300">{localizedTotals.appliedBalance.amount}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span>Billing cycle</span>
                        <span className="font-semibold capitalize text-slate-200">
                          {localizedRecurring?.interval || "month"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Charge currency</span>
                        <span className="font-semibold uppercase text-slate-200">{localizedSession.currency}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-3 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-400" aria-hidden="true" />
                    Loading localized order amounts...
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-end justify-between">
                <div>
                  <p className="text-sm text-slate-400">Due today</p>
                  <p className="mt-1 text-xs text-slate-500">Renews monthly until canceled</p>
                </div>
                <p className="text-3xl font-bold tracking-tight text-white">
                  {localizedTotals?.total?.amount || "Loading..."}
                </p>
              </div>
              {localizedRecurring?.dueNext?.total?.amount && (
                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span>Next recurring charge</span>
                  <span>{localizedRecurring.dueNext.total.amount}</span>
                </div>
              )}

              <ul className="mt-7 space-y-3">
                {(displayPlan.features || selectedPlan.features).map((feature) => (
                  <li key={feature} className="flex gap-3 text-sm leading-6 text-slate-300">
                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                      <Check className="h-3 w-3" aria-hidden="true" />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="mt-7 rounded-2xl border border-slate-800 bg-slate-950/35 p-4 text-xs leading-5 text-slate-500">
                Subscription charges are non-refundable. You may cancel at any
                time and retain paid access until the end of the current billing
                period. Flowguard does not provide prorated refunds. Starter
                subscriptions may upgrade to Pro immediately; downgrades take
                effect only after cancellation and the current paid period ends.
              </div>
            </div>
          </aside>
        </div>

        <footer className="mt-10 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-slate-500">
          <a href={`${PUBLIC_SITE_URL}/terms`} className="transition hover:text-slate-300">Terms</a>
          <a href={`${PUBLIC_SITE_URL}/privacy`} className="transition hover:text-slate-300">Privacy</a>
          <span>Payments securely processed by Stripe</span>
        </footer>
      </main>
    </div>
  );
}
